import { useCallback, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { MultiPick, type MultiPickOption, Kbd, KbdGroup, CmdCtrl } from '@duck/ui'
import {
  Play,
  SkipForward,
  SquarePlus,
  Code,
  ChevronsUpDown,
  Copy,
  Plus,
  Database,
  Trash2,
} from 'lucide-react'
import { useTar } from '@/modules'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { trackWorkspaceSettings } from '@/modules/workspaceSettingsAnalytics'
import { workspaceChallengeAtom } from '@/store/workspaceAtoms'
import { PillButtons } from '@/components/PythonNotebook/PillButtons'
import {
  PILL_BUTTON_DEFAULTS,
  PILL_BUTTON_ORDER,
  isPillButtonId,
  type PillButtonId,
} from '@/components/PythonNotebook/notebookPillConfig'
import type { TranslationKey } from '@/modules/i18n'

interface PythonPanelProps {
  onChange?: () => void
}

const BUTTON_LABEL_KEYS: Record<PillButtonId, TranslationKey> = {
  'run': 'common.settingsPythonRunLabel',
  'run-advance': 'common.settingsPythonRunAdvanceLabel',
  'run-insert': 'common.settingsPythonRunInsertLabel',
  'type': 'common.settingsPythonTypeLabel',
  'move': 'common.settingsPythonMoveLabel',
  'duplicate': 'common.settingsPythonDuplicateLabel',
  'add-below': 'common.settingsPythonAddBelowLabel',
  'inject': 'common.settingsPythonInjectLabel',
  'delete': 'common.settingsPythonDeleteLabel',
}

const BUTTON_SHORTCUTS: Partial<Record<PillButtonId, React.ReactNode>> = {
  'run': (
    <KbdGroup>
      <CmdCtrl />
      <Kbd>↵</Kbd>
    </KbdGroup>
  ),
  'run-advance': (
    <KbdGroup>
      <Kbd>Shift</Kbd>
      <Kbd>↵</Kbd>
    </KbdGroup>
  ),
  'run-insert': (
    <KbdGroup>
      <Kbd>Alt</Kbd>
      <Kbd>↵</Kbd>
    </KbdGroup>
  ),
}

const BUTTON_ICONS: Record<PillButtonId, React.ReactNode> = {
  'run': <Play className="h-3.5 w-3.5 text-emerald-500" fill="currentColor" />,
  'run-advance': <SkipForward className="h-3.5 w-3.5 text-emerald-500" fill="currentColor" />,
  'run-insert': <SquarePlus className="h-3.5 w-3.5 text-emerald-500" />,
  'type': <Code className="h-3.5 w-3.5" />,
  'move': <ChevronsUpDown className="h-3.5 w-3.5" />,
  'duplicate': <Copy className="h-3.5 w-3.5" />,
  'add-below': <Plus className="h-3.5 w-3.5" />,
  'inject': <Database className="h-3.5 w-3.5" />,
  'delete': <Trash2 className="h-3.5 w-3.5 text-red-500" />,
}

export const PythonPanel = ({ onChange }: PythonPanelProps) => {
  const { t } = useTar()
  const { getSetting, setSetting } = useLocalSettings()
  const challenge = useAtomValue(workspaceChallengeAtom)

  const enabled = (getSetting('notebookPillButtons') ?? PILL_BUTTON_DEFAULTS) as readonly PillButtonId[]

  const options: MultiPickOption[] = useMemo(
    () =>
      PILL_BUTTON_ORDER.map((id) => ({
        value: id,
        label: t(BUTTON_LABEL_KEYS[id]),
        icon: BUTTON_ICONS[id],
        trailing: BUTTON_SHORTCUTS[id],
      })),
    [t],
  )

  const handlePickerChange = useCallback(
    (next: string[]) => {
      const validNext = next.filter(isPillButtonId)
      const ordered = PILL_BUTTON_ORDER.filter((b) => validNext.includes(b))
      const prev = new Set(enabled)
      const nextSet = new Set(ordered)

      for (const id of PILL_BUTTON_ORDER) {
        const wasOn = prev.has(id)
        const isOn = nextSet.has(id)
        if (wasOn !== isOn) {
          trackWorkspaceSettings('workspace_settings:python:toggle_button', {
            challengeId: challenge?.id,
            buttonId: id,
            enabled: isOn,
          })
        }
      }

      setSetting('notebookPillButtons', ordered)
      onChange?.()
    },
    [enabled, setSetting, challenge?.id, onChange],
  )

  return (
    <div>
      <div className="mb-1 text-lg font-semibold text-foreground">
        {t('common.settingsPythonTitle')}
      </div>
      <div className="mb-5 text-xs text-muted-foreground">
        {t('common.settingsPythonSubtitle')}
      </div>

      {/* Live preview */}
      <div className="mb-3">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {t('common.settingsPythonExampleLabel')}
        </div>
        <div className="rounded-md border border-border bg-muted/20 px-3 py-6 flex justify-center">
          <div className="notebook-cell-pill inline-flex items-center rounded-md border border-border bg-card p-0.5 shadow-lg">
            <PillButtons mode="preview" enabled={enabled} />
            {/* Show the Customize cog in the preview too, but as a static placeholder (no-op). */}
            <span className="mx-0.5 h-4 w-px bg-border" />
            <button
              type="button"
              tabIndex={-1}
              aria-hidden
              className="inline-flex h-6 w-7 items-center justify-center rounded text-zinc-500 cursor-default"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Compact picker — popover keeps the panel short while toggling */}
      <MultiPick
        options={options}
        selected={[...enabled]}
        onChange={handlePickerChange}
        searchable={false}
        concatLabelsMaxLength={60}
        labels={{
          placeholder: t('common.settingsPythonPickerPlaceholder'),
          itemsLabel: t('common.settingsPythonPickerItemsLabel'),
        }}
      />
    </div>
  )
}

export default PythonPanel
